import { Inline } from '@bedrock-layout/primitives';
import { useAtom } from 'jotai';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { useEffect, useRef, useState, useTransition } from 'react';
import type { UseControllerProps, UseFormSetValue } from 'react-hook-form';
import { useController, useWatch } from 'react-hook-form';
import { ExecutedValueTooltip, TextInput, Typography } from 'ui';
import { IconByDataType } from 'ui/src/ConditionPopovers/RulePopover/IconByDataType';

import { predefineTokenDatasetAtom } from '../../../../../atom';
import {
  getTokenModuleName,
  isValidSchemaId,
} from '../../../../../components/TokenComponents/utils/helperFunction';
import { isRuleReadOnlyAtom } from '../../../index';
import { getRequiredKey } from '../../../utils/common';
import {
  ColumnHeaderContainer,
  HeaderInputContainer,
  ModuleSchemaFieldContainer,
  ResultBar,
  ResultHeader,
} from './DecisionTableBlock.styled';
import { ResultActions } from './ResultActions';
import { ResultAddMenu } from './ResultAddMenu';
import { SwitchToFormulaMenu } from './SwitchToFormulaMenu';

type ResultBlockProps = Omit<UseControllerProps, 'name'> & {
  id: string;
  isFirst: boolean;
  isLast: boolean;
  setValue: UseFormSetValue<any>;
  index: number;
  indexToObserve: number;
  setIndexToObserve: (index: number) => void;
  showBlank?: boolean;
  total: number;
};

export default function ResultBlock({
  id,
  isFirst,
  isLast,
  control,
  setValue,
  index,
  showBlank = false,
  total = 0,
  indexToObserve,
  setIndexToObserve,
}: ResultBlockProps) {
  const results = useWatch({ control, name: 'results' });
  const rows = useWatch({ control, name: 'rows' });
  const [isPending, startTransition] = useTransition();
  const [showResultActions, setShowResultActions] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const currentProperty = results[index];

  const currentKey = _isNil(currentProperty)
    ? undefined
    : Object.keys(currentProperty).filter((key) => key !== 'id')[0];

  const [isRuleReadOnly] = useAtom(isRuleReadOnlyAtom);
  const [predefineTokenDataset] = useAtom(predefineTokenDatasetAtom);

  useEffect(() => {
    if (index === indexToObserve && index !== -1) {
      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });

      setIndexToObserve(-1);
    }
  }, [indexToObserve]);

  const toggleResultActions = () => setShowResultActions(!showResultActions);

  const keyNameLocal = useWatch({
    name: `results.${index}.${currentKey ?? ''}.keyName`,
    control,
  });

  const [localValue, setLocalValue] = useState('');

  useEffect(() => {
    if (keyNameLocal !== localValue) {
      setLocalValue(keyNameLocal);
    }
  }, [JSON.stringify(keyNameLocal)]);

  const { fieldState } = useController({
    // eslint-disable-next-line
    name: `results.${index}.${currentKey}.keyName`,
    control,
  });

  const currentPropertyObj =
    !_isNil(currentProperty) && !_isNil(currentKey)
      ? currentProperty[currentKey]
      : null;

  const isModuleSchemaField =
    !_isNil(currentPropertyObj?.schemaId) &&
    !_isEmpty(currentPropertyObj?.schemaId) &&
    isValidSchemaId(predefineTokenDataset, currentPropertyObj?.schemaId);

    const handleSwitchToFormula = (dataType: string) => {
      startTransition(() => {
        const updates = [];
        
        for (let i = 0; i < rows?.length || 0; i++) {
          const row = rows[i];
          const rowKey = getRequiredKey(row, ['id']);
          const currentGroup = row[rowKey];
          
          if (_isNil(currentGroup) || _isEmpty(currentGroup)) {
            continue;
          }
          
          const ruleResult = currentGroup.ruleResult;
          const updatedRuleResult = [...ruleResult]; // Shallow copy
          
          const currResult = ruleResult[index];
          if (currResult) {
            const fieldKey = getRequiredKey(currResult, ['id']);
            updatedRuleResult[index] = {
              [fieldKey]: {
                ...currResult[fieldKey],
                dataType,
                key: "",
                executedValue: "",
                returnType: "",
                source: null,
                attribute: null,
                value: null,
              },
            };
            
            updates.push({
              path: `rows.${i}.${rowKey}.ruleResult`,
              value: updatedRuleResult
            });
          }
        }
        
        const newRows = [...rows];
        
        updates.forEach(update => {
          const pathParts = update.path.split('.');
          const rowIndex = parseInt(pathParts[1]);
          const rowKey = pathParts[2];
          
          if (newRows[rowIndex] && newRows[rowIndex][rowKey]) {
            newRows[rowIndex][rowKey].ruleResult = update.value;
          }
        });
        
        setValue('rows', newRows);
      });
    };

  return (
    <ResultHeader
      ref={containerRef}
      padding={isLast ? ['0.8rem', '1.6rem', '0.8rem'] : '0.8rem'}
    >
      <ColumnHeaderContainer>
        <ResultBar
          justify="start"
          align="center"
          stretch="start"
          onMouseEnter={toggleResultActions}
          onMouseLeave={toggleResultActions}
        >
          <Typography fontWeight={700} style={{ textAlign: 'center' }}>
            Result
          </Typography>

          {showResultActions && !showBlank && (
            <ResultActions
              control={control}
              setValue={setValue}
              index={index}
            />
          )}

          <ResultAddMenu
            setValue={setValue}
            control={control}
            index={index}
            total={total}
            setIndexToObserve={setIndexToObserve}
          />
        </ResultBar>

        {!showBlank && !_isNil(currentKey) && !_isEmpty(currentKey) && (
          <HeaderInputContainer>
            {isModuleSchemaField ? (
              <ExecutedValueTooltip
                id={currentKey}
                showExecutedValue={false}
                value={currentPropertyObj?.keyName}
                attribute={currentPropertyObj?.keyName}
                dataType={currentPropertyObj?.dataType}
                source={getTokenModuleName(
                  predefineTokenDataset,
                  currentPropertyObj?.schemaId
                )}
                placement="top"
              >
                <ModuleSchemaFieldContainer>
                  <Inline align="center" justify="center" gutter="0.5rem">
                    <IconByDataType
                      dataType={currentPropertyObj?.dataType ?? 'string'}
                      color="var(--color-paleDodgerBlue)"
                    />
                    <Typography>{localValue.length > 7? localValue.substring(0, 7) + '...' : localValue}</Typography>
                  </Inline>
                  {!['jsFormula', 'excelFormula'].includes(
                    currentPropertyObj?.dataType ?? 'string'
                  ) && (
                    <SwitchToFormulaMenu onMenuClick={handleSwitchToFormula} />
                  )}
                </ModuleSchemaFieldContainer>
              </ExecutedValueTooltip>
            ) : (
              <TextInput
                style={{ fontSize : '1.2rem'}}
                onChange={(e) => {
                  setLocalValue(e.target.value);
                }}
                onBlur={() => {
                  if (localValue !== keyNameLocal) {
                    setValue(
                      `results.${index}.${currentKey}.keyName`,
                      localValue
                    );
                  }
                }}
                size="small"
                value={localValue}
                // isSmallInput
                showErrorIcon={false}
                disabled={isRuleReadOnly}
                // eslint-disable-next-line
                hasError={!!fieldState.error?.message}
                startIcon={
                  <span>
                    <IconByDataType
                      dataType={currentPropertyObj?.dataType ?? 'string'}
                      color="var(--color-paleDodgerBlue)"
                    />
                  </span>
                }
                icon={
                  !['jsFormula', 'excelFormula'].includes(
                    currentPropertyObj?.dataType ?? 'string'
                  ) ? (
                    <SwitchToFormulaMenu onMenuClick={handleSwitchToFormula} />
                  ) : null
                }
                iconStyleClassName="text-input-trail-icon-style"
              />
            )}

            {
              // eslint-disable-next-line
              !!fieldState.error?.message && (
                <Typography name="errorXs">
                  {fieldState.error?.message ?? ''}
                </Typography>
              )
            }
          </HeaderInputContainer>
        )}
      </ColumnHeaderContainer>
    </ResultHeader>
  );
}
